import { memo, useState, useCallback, useMemo } from 'react';
import { Zap, Send, Trash2, Check, X, Edit3, Save } from 'lucide-react';

export const Ideas = memo(({ content, add, move, del, upd, user }) => {
  const [input, setInput] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  const ideas = useMemo(() => content.filter(c => c.status === 'idea'), [content]);
  const pending = useMemo(() => content.filter(c => c.status === 'pending_approval'), [content]);

  const submit = useCallback(() => {
    if (input.trim()) {
      add(input.trim(), urgent);
      setInput('');
      setUrgent(false);
    }
  }, [input, urgent, add]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') submit();
  }, [submit]);

  const startEdit = useCallback((c) => {
    setEditingId(c.id);
    setEditTitle(c.title);
  }, []);

  const saveEdit = useCallback(() => {
    if (editTitle.trim()) {
      upd(editingId, { title: editTitle.trim() });
    }
    setEditingId(null);
    setEditTitle('');
  }, [editingId, editTitle, upd]);

  const handleEditKeyDown = useCallback((e) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') { setEditingId(null); setEditTitle(''); }
  }, [saveEdit]);

  return (
    <div className="space-y-6 max-w-4xl">
      <h2 className="text-2xl font-bold text-slate-800">Ideas Bank</h2>

      {/* Quick Add */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 md:p-6">
        <div className="flex gap-2 md:gap-3">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="New idea..."
            className="flex-1 border-2 border-slate-200 rounded-xl px-3 md:px-4 py-2.5 md:py-3 focus:outline-none focus:border-violet-400"
          />
          <button
            onClick={() => setUrgent(!urgent)}
            className={`px-3 md:px-5 py-2.5 md:py-3 rounded-xl font-semibold flex items-center gap-2 ${
              urgent ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'
            }`}
          >
            <Zap className="w-5 h-5" />
            <span className="hidden sm:inline">{urgent ? 'Urgent' : 'Normal'}</span>
          </button>
          <button
            onClick={submit}
            className="px-4 md:px-6 py-2.5 md:py-3 bg-violet-600 text-white rounded-xl font-semibold hover:bg-violet-700"
          >
            Add
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {/* Ideas List */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 md:p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-bold text-lg text-slate-800">All Ideas</h3>
            <span className="text-slate-400 font-medium">{ideas.length}</span>
          </div>
          {ideas.length === 0 ? (
            <p className="text-slate-400 text-center py-10">No ideas yet</p>
          ) : (
            <div className="space-y-3 max-h-[450px] overflow-y-auto">
              {ideas.map(c => (
                <div key={c.id} className="p-4 bg-slate-50 rounded-xl">
                  {editingId === c.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        onKeyDown={handleEditKeyDown}
                        className="flex-1 border-2 border-violet-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
                      />
                      <button onClick={saveEdit} className="p-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700" title="Save">
                        <Save className="w-4 h-4" />
                      </button>
                      <button onClick={() => setEditingId(null)} className="p-2 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300" title="Cancel">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-800 truncate">{c.title}</p>
                          {c.urgent && <Zap className="w-4 h-4 text-red-500 flex-shrink-0" />}
                        </div>
                        <p className="text-sm text-slate-400 capitalize">by {c.by}</p>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button onClick={() => startEdit(c)} className="p-2 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300" title="Edit">
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button onClick={() => move(c.id, 'pending_approval')} className="p-2 bg-amber-100 text-amber-600 rounded-lg hover:bg-amber-200" title="Submit for approval">
                          <Send className="w-4 h-4" />
                        </button>
                        <button onClick={() => del(c.id)} className="p-2 bg-red-50 text-red-500 rounded-lg hover:bg-red-100" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pending Approval */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 md:p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-bold text-lg text-slate-800">Pending Approval</h3>
            {pending.length > 0 && (
              <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-semibold">
                {pending.length}
              </span>
            )}
          </div>
          {pending.length === 0 ? (
            <p className="text-slate-400 text-center py-10">None pending</p>
          ) : (
            <div className="space-y-3 max-h-[450px] overflow-y-auto">
              {pending.map(c => (
                <div key={c.id} className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 truncate">{c.title}</p>
                    <p className="text-sm text-slate-400">from Mo</p>
                  </div>
                  {user === 'zein' && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => move(c.id, 'approved')} className="p-2.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200" title="Approve">
                        <Check className="w-5 h-5" />
                      </button>
                      <button onClick={() => move(c.id, 'idea')} className="p-2.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200" title="Reject">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

Ideas.displayName = 'Ideas';
